import { Weapon, getWeaponById } from './weapon';

export interface Fighter {
  id: string;
  name: string;
  health: number;
  maxHealth: number;
  stamina: number;
  maxStamina: number;
  leftHand: Weapon | null;
  rightHand: Weapon | null;
  armor: Weapon | null; // Доспехи (пассивная защита)
  isDefending: boolean; // Активная защита щитом
  x: number;
  y: number;
  isPlayer: boolean; // true для игрока, false для ИИ
  rank: number; // 0 - новобранец, 1 - боец, 2 - ветеран
  rankPoints: number; // очки за победы
}

export function createFighter(
  id: string,
  name: string,
  x: number,
  y: number,
  isPlayer: boolean = true,
  leftHandId?: number,
  rightHandId?: number,
  armorId?: number
): Fighter {
  const leftHand = leftHandId !== undefined ? getWeaponById(leftHandId) || null : null;
  const rightHand = rightHandId !== undefined ? getWeaponById(rightHandId) || null : null;
  const armor = armorId !== undefined ? getWeaponById(armorId) || null : null;

  // Для орков определяем ранг на основе экипировки
  let orcRank = 0;
  if (!isPlayer) {
    // Определяем максимальный ранг из всей экипировки
    const ranks = [
      leftHand?.rankRequired,
      rightHand?.rankRequired,
      armor?.rankRequired
    ].filter((r): r is number => r !== undefined);
    
    if (ranks.length > 0) {
      orcRank = Math.max(...ranks);
    }
    // Если нет ранга у оружия/брони/щита - это молодой орк (ранг 0)
  }

  // Рассчитываем вес экипировки
  let totalWeight = 0;
  if (leftHand) totalWeight += leftHand.weight;
  if (rightHand) totalWeight += rightHand.weight;
  if (armor) totalWeight += armor.weight;

  // Для орков: базовая стамина зависит от ранга, затем вычитается вес экипировки
  // Ранг 0 (молодой) - 10, ранг 1 (боец) - 12, ранг 2 (ветеран) - 14
  // Для людей: базовая стамина 10 + 2 за ранг, минус вес
  let baseStamina: number;
  if (!isPlayer) {
    // Орки: базовая стамина от ранга (10/12/14), затем минус вес экипировки
    const orcBaseStamina = orcRank === 0 ? 10 : orcRank === 1 ? 12 : 14;
    baseStamina = orcBaseStamina - totalWeight;
  } else {
    // Люди: базовая стамина 10, минус вес экипировки
    baseStamina = 10 - totalWeight;
  }

  const calculatedMaxStamina = Math.max(1, baseStamina);

  // Здоровье зависит от ранга: ранг 0 = 10, ранг 1 = 12, ранг 2 = 14
  const fighterRank = isPlayer ? 0 : orcRank; // Для людей ранг устанавливается отдельно при повышении
  const calculatedMaxHealth = 10 + fighterRank * 2;

  return {
    id,
    name,
    health: calculatedMaxHealth,
    maxHealth: calculatedMaxHealth,
    stamina: calculatedMaxStamina,
    maxStamina: calculatedMaxStamina,
    leftHand,
    rightHand,
    armor,
    isDefending: false,
    x,
    y,
    isPlayer,
    rank: fighterRank,
    rankPoints: 0
  };
}

export function getFighterMaxHealth(fighter: Fighter): number {
  // Базовое 10 + 2 за каждый ранг выше 0
  return 10 + fighter.rank * 2;
}

export function getFighterMaxStamina(fighter: Fighter): number {
  // Для орков: базовая стамина зависит от ранга экипировки, затем вычитается вес
  if (!fighter.isPlayer) {
    // Определяем максимальный ранг из всей экипировки
    const ranks = [
      fighter.leftHand?.rankRequired,
      fighter.rightHand?.rankRequired,
      fighter.armor?.rankRequired
    ].filter((r): r is number => r !== undefined);
    
    const orcRank = ranks.length > 0 ? Math.max(...ranks) : 0;
    // Базовая стамина: ранг 0 (молодой) - 10, ранг 1 (боец) - 12, ранг 2 (ветеран) - 14
    const orcBaseStamina = orcRank === 0 ? 10 : orcRank === 1 ? 12 : 14;
    
    // Вычитаем вес экипировки
    let totalWeight = 0;
    if (fighter.leftHand) totalWeight += fighter.leftHand.weight;
    if (fighter.rightHand) totalWeight += fighter.rightHand.weight;
    if (fighter.armor) totalWeight += fighter.armor.weight;
    
    return Math.max(1, orcBaseStamina - totalWeight);
  }
  
  // Для людей: базовое 10 + 2 за каждый ранг выше 0, минус вес
  const baseStamina = 10 + fighter.rank * 2;
  // Считаем общий вес
  let totalWeight = 0;
  if (fighter.leftHand) totalWeight += fighter.leftHand.weight;
  if (fighter.rightHand) totalWeight += fighter.rightHand.weight;
  if (fighter.armor) totalWeight += fighter.armor.weight;

  return Math.max(1, baseStamina - totalWeight);
}

export function getFighterDefense(fighter: Fighter): number {
  let defense = 0;

  // Пассивная защита от доспехов (всегда активна)
  if (fighter.armor) {
    defense += fighter.armor.passiveDefense;
  }

  if (fighter.isDefending) {
    // Активная защита щитом
    if (fighter.leftHand && fighter.leftHand.activeDefense > 0) {
      defense += fighter.leftHand.activeDefense;
    }
    if (fighter.rightHand && fighter.rightHand.activeDefense > 0) {
      defense += fighter.rightHand.activeDefense;
    }
  } else {
    // Пассивная защита от щитов (если не активная защита)
    if (fighter.leftHand && fighter.leftHand.passiveDefense > 0) {
      defense += fighter.leftHand.passiveDefense;
    }
    if (fighter.rightHand && fighter.rightHand.passiveDefense > 0) {
      defense += fighter.rightHand.passiveDefense;
    }
  }

  return defense;
}

// Получает только пассивную защиту (броню) - от доспехов и щитов (без активной защиты)
export function getFighterPassiveDefense(fighter: Fighter): number {
  let defense = 0;

  // Пассивная защита от доспехов (всегда активна)
  if (fighter.armor) {
    defense += fighter.armor.passiveDefense;
  }

  // Пассивная защита от щитов (если не активная защита)
  if (!fighter.isDefending) {
    if (fighter.leftHand && fighter.leftHand.passiveDefense > 0) {
      defense += fighter.leftHand.passiveDefense;
    }
    if (fighter.rightHand && fighter.rightHand.passiveDefense > 0) {
      defense += fighter.rightHand.passiveDefense;
    }
  }

  return defense;
}

// Получает только активную защиту от щита (когда isDefending = true)
export function getFighterActiveDefense(fighter: Fighter): number {
  if (!fighter.isDefending) return 0;
  
  let defense = 0;
  if (fighter.leftHand && fighter.leftHand.activeDefense > 0) {
    defense += fighter.leftHand.activeDefense;
  }
  if (fighter.rightHand && fighter.rightHand.activeDefense > 0) {
    defense += fighter.rightHand.activeDefense;
  }

  return defense;
}

export function getFighterAttack(fighter: Fighter): number {
  let attack = 0;
  if (fighter.leftHand && fighter.leftHand.attack > 0) {
    attack += fighter.leftHand.attack;
  }
  if (fighter.rightHand && fighter.rightHand.attack > 0) {
    attack += fighter.rightHand.attack;
  }
  return attack;
}

export function getFighterRange(fighter: Fighter): number {
  let range = 1; // Минимальная дальность
  if (fighter.leftHand && fighter.leftHand.range > range) {
    range = fighter.leftHand.range;
  }
  if (fighter.rightHand && fighter.rightHand.range > range) {
    range = fighter.rightHand.range;
  }
  return range;
}

export function canFighterAttack(fighter: Fighter): boolean {
  const hasWeapon = (fighter.leftHand && fighter.leftHand.attack > 0) ||
    (fighter.rightHand && fighter.rightHand.attack > 0);
  if (!hasWeapon) return false;

  const attackCost = getAttackCost(fighter);
  return fighter.stamina >= attackCost;
}

export function getAttackCost(fighter: Fighter): number {
  // Используем максимальную стоимость атаки из оружия
  let cost = 0;
  if (fighter.leftHand && fighter.leftHand.attack > 0) {
    cost = Math.max(cost, fighter.leftHand.actionPoints);
  }
  if (fighter.rightHand && fighter.rightHand.attack > 0) {
    cost = Math.max(cost, fighter.rightHand.actionPoints);
  }
  return cost || 1;
}

export function getDefenseCost(fighter: Fighter): number {
  // Используем максимальную стоимость защиты из щитов
  let cost = 0;
  if (fighter.leftHand && fighter.leftHand.activeDefense > 0) {
    cost = Math.max(cost, fighter.leftHand.actionPoints);
  }
  if (fighter.rightHand && fighter.rightHand.activeDefense > 0) {
    cost = Math.max(cost, fighter.rightHand.actionPoints);
  }
  return cost || 0;
}

// Определяет имя файла спрайта для ЛЮДЕЙ на основе экипировки
// Возвращает только имя файла (без пути), путь добавляется в компонентах
export function getFighterSprite(fighter: Fighter): string {
  const leftId = fighter.leftHand?.id;
  const rightId = fighter.rightHand?.id;
  const armorId = fighter.armor?.id;

  // Определяем оружие
  const hasClub = leftId === 0 || rightId === 0; // Дубина (id: 0)
  const hasSmallSword = leftId === 1 || rightId === 1; // Меч малый (id: 1)
  const hasLargeSword = leftId === 2 || rightId === 2; // Меч большой (id: 2)
  const hasSmallBow = leftId === 5 || rightId === 5; // Лук малый (id: 5)
  const hasLargeBow = leftId === 6 || rightId === 6; // Лук большой (id: 6)

  // Определяем щиты
  const hasWoodShield = leftId === 7 || rightId === 7; // Щит деревянный (id: 7)
  const hasSmallShield = leftId === 3 || rightId === 3; // Щит малый (id: 3)
  const hasLargeShield = leftId === 4 || rightId === 4; // Щит большой (id: 4)

  // Определяем доспехи
  const hasLeatherArmor = armorId === 8; // Кожаные доспехи (id: 8)
  const hasPlateArmor = armorId === 9; // Металлические доспехи (id: 9)

  // Суффикс для доспехов
  const armorSuffix = hasPlateArmor ? '_plate' : hasLeatherArmor ? '_leather' : '';

  // ТРОЙНЫЕ КОМБИНАЦИИ: Оружие + Щит + Доспехи (приоритет 1)
  if (hasClub && hasSmallShield && armorSuffix) {
    return `clubman_shield_small${armorSuffix}.png`;
  }
  if (hasSmallSword && hasSmallShield && armorSuffix) {
    return `swordsman_shield_small${armorSuffix}.png`;
  }
  if (hasSmallSword && hasLargeShield && armorSuffix) {
    return `swordsman_shield_great${armorSuffix}.png`;
  }

  // КОМБИНАЦИИ: Оружие + Щит (приоритет 2)
  // Проверяем комбинации с приоритетом: сначала малый щит, потом большой, потом деревянный
  if (hasClub && hasSmallShield) {
    return 'clubman_shield_small.png';
  }
  if (hasClub && hasLargeShield) {
    return 'clubman_shield_great.png';
  }
  if (hasClub && hasWoodShield) {
    return 'clubman_shield_wood.png';
  }
  if (hasSmallSword && hasSmallShield) {
    return 'swordsman_shield_small.png';
  }
  if (hasSmallSword && hasLargeShield) {
    return 'swordsman_shield_great.png';
  }
  if (hasSmallSword && hasWoodShield) {
    return 'swordsman_shield_wood.png';
  }

  // КОМБИНАЦИИ: Оружие + Доспехи (приоритет 3)
  if (hasClub && armorSuffix) {
    return `clubman${armorSuffix}.png`;
  }
  if (hasSmallSword && armorSuffix) {
    return `swordsman${armorSuffix}.png`;
  }
  if (hasLargeSword && armorSuffix) {
    return `swordsman_great${armorSuffix}.png`;
  }
  if (hasSmallBow && armorSuffix) {
    return `archer_small${armorSuffix}.png`;
  }
  if (hasLargeBow && armorSuffix) {
    return `archer_great${armorSuffix}.png`;
  }

  // ОДИНОЧНОЕ ОРУЖИЕ (приоритет 4)
  if (hasClub) {
    return 'clubman.png';
  }
  if (hasSmallSword) {
    return 'swordsman_small.png';
  }
  if (hasLargeSword) {
    return 'swordsman_great.png';
  }
  if (hasSmallBow) {
    return 'archer_small.png';
  }
  if (hasLargeBow) {
    return 'archer_great.png';
  }

  // Только щиты или доспехи (редкие случаи)
  if (hasWoodShield) {
    return 'shieldman_wood.png';
  }
  if (hasSmallShield) {
    return 'shieldman_small.png';
  }
  if (hasLargeShield) {
    return 'shieldman_great.png';
  }
  if (hasLeatherArmor) {
    return 'leather_armor.png';
  }
  if (hasPlateArmor) {
    return 'plate_armor.png';
  }

  // По умолчанию - новобранец без оружия
  return 'clubman.png';
}

// Определяет имя файла спрайта для ОРКОВ на основе экипировки
// Возвращает только имя файла (без пути), путь добавляется в компонентах
export function getOrcSprite(fighter: Fighter): string {
  const leftId = fighter.leftHand?.id;
  const rightId = fighter.rightHand?.id;
  const armorId = fighter.armor?.id;

  // Определяем оружие орков (ID будут определены позже, пока используем заглушки)
  // Костяной кинжал (id: 10)
  const hasBoneKnife = leftId === 10 || rightId === 10;
  // Ржавый топор (id: 11)
  const hasRustAxe = leftId === 11 || rightId === 11;
  // Каменная булава (id: 12)
  const hasStoneClub = leftId === 12 || rightId === 12;
  // Томагавк (id: 16)
  const hasTomahawk = leftId === 16 || rightId === 16;
  // Копьё (id: 17)
  const hasSpear = leftId === 17 || rightId === 17;

  // Определяем щиты орков
  // Костяной нагрудник (id: 13)
  const hasBoneChest = leftId === 13 || rightId === 13;
  // Деревянный щит орка (id: 14)
  const hasWoodShield = leftId === 14 || rightId === 14;
  // Каменный щит (id: 15)
  const hasStoneShield = leftId === 15 || rightId === 15;

  // Определяем доспехи орков
  // Кожа мутанта (id: 18)
  const hasMutantSkin = armorId === 18;
  // Костяные доспехи (id: 19)
  const hasBoneArmor = armorId === 19;

  // Суффикс для доспехов
  const armorSuffix = hasBoneArmor ? '_bone_armor' : hasMutantSkin ? '_mutant' : '';

  // ТРОЙНЫЕ КОМБИНАЦИИ: Оружие + Щит + Доспехи (приоритет 1)
  if (hasBoneKnife && hasBoneChest && armorSuffix) {
    return `bone_knifeman_chest${armorSuffix}.png`;
  }
  if (hasRustAxe && hasWoodShield && armorSuffix) {
    return `axe_shield_orc${armorSuffix}.png`;
  }
  if (hasStoneClub && hasStoneShield && armorSuffix) {
    return `stone_club_shield${armorSuffix}.png`;
  }
  if (hasBoneKnife && hasWoodShield && armorSuffix) {
    return `bone_knifeman_shield_wood${armorSuffix}.png`;
  }
  if (hasBoneKnife && hasStoneShield && armorSuffix) {
    return `bone_knifeman_shield_stone${armorSuffix}.png`;
  }
  if (hasRustAxe && hasStoneShield && armorSuffix) {
    return `rust_axe_shield_stone${armorSuffix}.png`;
  }
  if (hasStoneClub && hasWoodShield && armorSuffix) {
    // Файлов stone_clubber_shield_wood_mutant.png и stone_clubber_shield_wood_bone_armor.png нет
    // Используем stone_club_shield.png
    return 'stone_club_shield.png';
  }

  // КОМБИНАЦИИ: Оружие + Щит (приоритет 2)
  if (hasBoneKnife && hasBoneChest) {
    return 'bone_knifeman_chest.png';
  }
  if (hasBoneKnife && hasWoodShield) {
    return 'bone_knifeman_shield_wood.png';
  }
  if (hasBoneKnife && hasStoneShield) {
    return 'bone_knifeman_shield_stone.png';
  }
  if (hasRustAxe && hasWoodShield) {
    return 'axe_shield_orc.png';
  }
  if (hasRustAxe && hasStoneShield) {
    return 'rust_axe_shield_stone.png';
  }
  if (hasStoneClub && hasWoodShield) {
    // Файла stone_clubber_shield_wood.png нет, используем stone_club_shield.png
    return 'stone_club_shield.png';
  }
  if (hasStoneClub && hasStoneShield) {
    return 'stone_club_shield.png';
  }

  // КОМБИНАЦИИ: Оружие + Доспехи (приоритет 3)
  if (hasBoneKnife && armorSuffix) {
    return `bone_knifeman${armorSuffix}.png`;
  }
  if (hasRustAxe && armorSuffix) {
    // Для мутанта: rust_axe_orc_mutant.png, для костяных доспехов: rust_axe_bone_armor.png
    if (hasMutantSkin) {
      return 'rust_axe_orc_mutant.png';
    }
    if (hasBoneArmor) {
      return 'rust_axe_bone_armor.png';
    }
  }
  if (hasStoneClub && armorSuffix) {
    // Файлов stone_clubber_mutant.png и stone_clubber_bone_armor.png нет
    // Используем базовый спрайт
    return 'stone_clubber.png';
  }
  if (hasTomahawk && armorSuffix) {
    // Файлов tomahawk_orc_mutant.png и tomahawk_orc_bone_armor.png нет
    // Используем базовый спрайт
    return 'tomahawk_orc.png';
  }
  if (hasSpear && armorSuffix) {
    // Файлов spear_orc_mutant.png и spear_orc_bone_armor.png нет
    // Используем базовый спрайт
    return 'spear_orc.png';
  }

  // ОДИНОЧНОЕ ОРУЖИЕ (приоритет 4)
  if (hasBoneKnife) {
    return 'bone_knifeman.png';
  }
  if (hasRustAxe) {
    return 'rust_axe_orc.png';
  }
  if (hasStoneClub) {
    return 'stone_clubber.png';
  }
  if (hasTomahawk) {
    return 'tomahawk_orc.png';
  }
  if (hasSpear) {
    return 'spear_orc.png';
  }

  // По умолчанию - базовый орк
  return 'bone_knifeman.png';
}

// Получает полный путь к спрайту бойца (с учетом папки human/ или orc/)
export function getFighterSpritePath(fighter: Fighter): string {
  const spriteName = fighter.isPlayer 
    ? getFighterSprite(fighter)
    : getOrcSprite(fighter);
  const folder = fighter.isPlayer ? 'human' : 'orc';
  const path = `./sprites/fighters/${folder}/${spriteName}`;
  
  // Логирование для отладки (можно убрать в продакшене)
  if (process.env.NODE_ENV === 'development') {
    console.log('Fighter sprite path:', {
      fighterId: fighter.id,
      isPlayer: fighter.isPlayer,
      leftHand: fighter.leftHand?.id,
      rightHand: fighter.rightHand?.id,
      armor: fighter.armor?.id,
      spriteName,
      path
    });
  }
  
  return path;
}

